import React, { useState, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { useInView } from 'react-intersection-observer';
import type * as Types from '@xingternal/pages-graphql-types';

import { Headline } from '@xingternal/typography';

import {
  trackNewsArticleClickAction,
  getIDfromURN,
} from '@xingternal/pages-tracking';
import Video from '@xingternal/video';

import {
  CONTENT_FEED_TYPE_ARTICLE,
  CONTENT_FEED_TYPE_NEW_ARTICLE,
  CONTENT_FEED_TYPE_GENERIC,
  CONTENT_FEED_TYPE_COMPANY_ARTICLE,
  ENTITY_PAGE_IMAGE_POSTING,
  VIDEOPOSTING,
  LINKSHAREPOSTING,
  IMAGEPOSTING,
} from '../constants';

import { EntityPageFeedCollectionFragment } from '../EntityPageFeedCollectionFragment.gql-types';

import styles from './styles.css';

const trimCopyText = (title: string) => title.length > 80;

type NewsItemAttachmentProps = {
  item: EntityPageFeedCollectionFragment['object'];
  isPinned: boolean;
  actorId: string;
  videoPlaying: React.MutableRefObject<any>;
};

const NewsItemAttachment = ({
  item,
  isPinned,
  actorId,
  videoPlaying,
}: NewsItemAttachmentProps) => {
  const [playerActions, setPlayerActions] = useState<any>();

  const [ref, inView] = useInView({
    rootMargin: '0px 0px',
    threshold: 0.5,
  });

  useEffect(() => {
    if (playerActions) {
      if (!inView) {
        playerActions && playerActions.pause(true);
      }
    }
  }, [inView]);

  const type = item?.__typename;

  // current types
  // linkshare
  // image posting
  // video posting
  // plain text

  // Normalize the Attachments to a single object with the same naming,
  // so its easier to render

  type attachmentType = Pick<
    Types.EntityPageContentFeedArticlePost,
    'globalId' | 'title' | 'description' | 'provider' | 'permalink' | 'comment'
  > & {
    type?: string;
    image?: string;
    url?: any;
    video?: Pick<Types.Video, 'id'>;
    videoV2?: Pick<Types.VideoV2, 'ref'>;
  };

  const attachment = useMemo(() => {
    let tempPostingAttachment: attachmentType | undefined = undefined;

    if (
      typeof item === 'undefined' ||
      item === null ||
      item.__typename === 'EntityPageContentFeedPlainTextPostingPost'
    ) {
      return;
    }

    if (
      (item?.__typename === 'EntityPageContentFeedCompanyArticlePost' ||
        item?.__typename === 'EntityPageContentFeedArticlePost') &&
      item.video
    ) {
      tempPostingAttachment = {
        globalId: item.globalId,
        type: VIDEOPOSTING,
        video: item.video,
        title: item.title,
        description: item.description,
        provider: item.provider ? item.provider : item.permalink,
        permalink: item.permalink,
        url: item.permalink,
      };
    }

    switch (type) {
      // company updates
      case CONTENT_FEED_TYPE_COMPANY_ARTICLE:
        // if it is a company video we dont want to show the original url
        if (tempPostingAttachment?.type === VIDEOPOSTING) {
          tempPostingAttachment.url = null;
          tempPostingAttachment.permalink = null;
          tempPostingAttachment.provider = null;
        }

        // check for linkshare posting
        if (!tempPostingAttachment?.type && item?.permalink) {
          tempPostingAttachment = {
            globalId: item.globalId,
            type: LINKSHAREPOSTING,
            url: item.permalink,
            image: item.images ? item.images?.[0].url : item.imageUrl,
            provider: item.provider ? item.provider : item.permalink,
            title: item.title,
            description: item.description,
          };
        }

        // check for image posting
        if (!tempPostingAttachment?.type && item?.images?.length) {
          tempPostingAttachment = {
            globalId: item.globalId,
            type: IMAGEPOSTING,
            image: item.images?.[0].url,
          };
        }
        break;

      // content article
      case CONTENT_FEED_TYPE_ARTICLE:
        if (!tempPostingAttachment?.type && item?.permalink) {
          tempPostingAttachment = {
            globalId: item.globalId,
            type: LINKSHAREPOSTING,
            url: item.permalink,
            image: isPinned
              ? item.image?.['srcWidth1232']
              : item.image?.['srcWidth516'],
            title: item.title,
            description: item.description,
            provider: item.provider,
          };
        }

        break;

      // content new article
      case CONTENT_FEED_TYPE_NEW_ARTICLE:
        if (!tempPostingAttachment?.type && item?.visitUrl) {
          tempPostingAttachment = {
            globalId: item.globalId,
            type: LINKSHAREPOSTING,
            url: item.visitUrl,
            image: item.images?.[0].url,
            title: item.title,
            description: item.summary,
          };
        }

        break;

      // posting
      case CONTENT_FEED_TYPE_GENERIC:
        //
        if (
          !tempPostingAttachment?.type &&
          item?.attachment?.__typename === 'PostingsVideoAttachment' &&
          item?.attachment?.videoV2
        ) {
          tempPostingAttachment = {
            globalId: item.globalId,
            type: VIDEOPOSTING,
            videoV2: item.attachment.videoV2,
            title: '',
            url: null,
          };
        }

        // we skip all of this if post has video
        if (
          !tempPostingAttachment?.type &&
          item?.attachment?.__typename === 'PostingsLinkAttachment' &&
          !!item?.attachment?.link
        ) {
          const { link } = item.attachment;

          tempPostingAttachment = {
            globalId: item.globalId,
            type: LINKSHAREPOSTING,
            url: link.url,
            image: link.cachedImageUrl,
            title: link.title || '',
            description: link.description || '',
            comment: item.comment || '',
            provider: link.sourceDomain || link.url,
          };
        }

        if (
          !tempPostingAttachment?.type &&
          item.attachment?.__typename === ENTITY_PAGE_IMAGE_POSTING
        ) {
          tempPostingAttachment = {
            globalId: item.globalId,
            type: IMAGEPOSTING,
            image: item.attachment?.images?.[0].url,
            comment: item.comment,
          };
        }
        break;
    }

    return tempPostingAttachment;
  }, [
    item?.__typename !== 'EntityPageContentFeedPlainTextPostingPost' &&
      item?.globalId,
  ]);

  const onPlayHandler = () => {
    // stop previous one
    if (
      videoPlaying?.current &&
      videoPlaying.current.id !== attachment?.globalId
    )
      videoPlaying.current.api.pause(true);
    // add current one to News state
    videoPlaying.current = { api: playerActions, id: attachment?.globalId };
  };

  const renderVideo = (postingAttachment: attachmentType) => {
    let videoRef = postingAttachment.video?.id;
    if (!videoRef) {
      videoRef = postingAttachment.videoV2?.ref;
    }
    return (
      <>
        {videoRef && (
          <section
            className={styles.video}
            ref={ref}
            style={{ opacity: inView ? 1 : 0.8 }}
          >
            <Video
              id={videoRef}
              section={'entity_pages_news_module'}
              actions={(api: any) => {
                setPlayerActions(api);
              }}
              onPlay={onPlayHandler}
            />
          </section>
        )}
        {postingAttachment.url && renderLinkshare(postingAttachment)}
      </>
    );
  };

  const renderLinkshare = (postingAttachment: attachmentType) => {
    const articleHasImage =
      postingAttachment.image !== null && postingAttachment.image !== undefined;
    const articleHasTextContent =
      postingAttachment.description !== null ||
      postingAttachment.description !== undefined;
    const isExternalUrl = !postingAttachment.provider?.includes('xing.com');
    const externalUrlProps = isExternalUrl
      ? {
          target: '_blank',
          rel: 'noreferrer noopener nofollow',
          onClick: () => {
            item?.__typename !== 'EntityPageContentFeedPlainTextPostingPost' &&
              trackNewsArticleClickAction(
                getIDfromURN(item?.globalId),
                getIDfromURN(actorId)
              );
          },
        }
      : {};

    return (
      <section
        className={cx(styles.content, {
          [styles.imageOnly]: articleHasImage && !articleHasTextContent,
          [styles.textOnly]: !articleHasImage && articleHasTextContent,
          [styles.isPinned]: isPinned,
        })}
      >
        <a
          href={postingAttachment.url}
          {...externalUrlProps}
          className={styles.globalPostingLink}
        />

        {articleHasImage && isPinned && (
          <a
            className={styles.contentImg}
            href={postingAttachment.url}
            {...externalUrlProps}
          >
            <img
              alt={postingAttachment.title || 'News item main image'}
              className={styles.topImage}
              src={postingAttachment.image}
              loading="lazy"
            />
          </a>
        )}
        {articleHasTextContent && (
          <section className={styles.contentText}>
            <Headline as="h3" className={styles.headline}>
              <a
                className={styles.headlineAnchor}
                href={postingAttachment.url}
                {...externalUrlProps}
              >
                {postingAttachment.title}
              </a>
            </Headline>

            {!!postingAttachment.description && postingAttachment.title && (
              <a
                {...externalUrlProps}
                href={postingAttachment.url}
                className={cx(styles.summary, {
                  [styles.summaryTrimmed]: trimCopyText(
                    postingAttachment.title
                  ),
                })}
              >
                {postingAttachment.description}
              </a>
            )}
            <span className={styles.provider}>
              <a href={postingAttachment.url} {...externalUrlProps}>
                {postingAttachment.provider}
              </a>
            </span>
          </section>
        )}

        {articleHasImage && !isPinned && (
          <a
            href={postingAttachment.url}
            {...externalUrlProps}
            className={styles.contentImg}
          >
            <img
              alt={postingAttachment.title || 'News item main image'}
              className={styles.contentImg}
              src={postingAttachment.image}
              loading="lazy"
            />
          </a>
        )}
      </section>
    );
  };

  const renderImagePosting = (postingAttachment: attachmentType) => (
    <section className={styles.imagePosting}>
      <img src={postingAttachment.image} loading="lazy" alt="Posting image" />
    </section>
  );

  return (
    <>
      {attachment && (
        <>
          {attachment.type === VIDEOPOSTING && renderVideo(attachment)}
          {attachment.type === LINKSHAREPOSTING && renderLinkshare(attachment)}
          {attachment.type === IMAGEPOSTING && renderImagePosting(attachment)}
        </>
      )}
    </>
  );
};

export default NewsItemAttachment;
